import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Trash2, Pencil, Plus, Link as LinkIcon, Copy } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", status: "lead", notes: "", contract_url: "", contract_status: "not_sent", pack_items: [], portal_token: "" };

export const ClientsManager = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: clients = [] } = useQuery({ queryKey: ["admin-clients"], queryFn: async () => (await api.get("/admin/clients")).data });

  const save = useMutation({
    mutationFn: async () =>
      editing
        ? api.put(`/admin/clients/${editing.id}`, form)
        : api.post("/admin/clients", { name: form.name, email: form.email, phone: form.phone, status: form.status, notes: form.notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-clients"] });
      toast("Cliente salvo!");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
    },
    onError: (e) => toast(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/clients/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-clients"] }); toast("Cliente removido."); },
  });

  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const addPackItem = () => setForm({ ...form, pack_items: [...form.pack_items, { title: "", url: "" }] });
  const updatePackItem = (i, key, val) => {
    const items = [...form.pack_items];
    items[i] = { ...items[i], [key]: val };
    setForm({ ...form, pack_items: items });
  };
  const removePackItem = (i) => setForm({ ...form, pack_items: form.pack_items.filter((_, idx) => idx !== i) });

  const portalUrl = (token) => `${window.location.origin}/portal/${token}`;
  const copyPortal = (token) => { navigator.clipboard.writeText(portalUrl(token)); toast("Link copiado!"); };

  return (
    <div className="mt-6" data-testid="clients-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="client-new-button"><Plus className="w-4 h-4 mr-1" /> Novo cliente</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Nome</TableHead><TableHead>E-mail</TableHead><TableHead>Status</TableHead><TableHead>Contrato</TableHead><TableHead>Portal</TableHead><TableHead /></TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((c) => (
            <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
              <TableCell>{c.name}</TableCell>
              <TableCell>{c.email}</TableCell>
              <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
              <TableCell><Badge variant="outline">{c.contract_status}</Badge></TableCell>
              <TableCell>
                <Button size="icon" variant="ghost" onClick={() => copyPortal(c.portal_token)} data-testid={`client-copy-portal-${c.id}`}>
                  <Copy className="w-4 h-4" />
                </Button>
              </TableCell>
              <TableCell className="flex gap-2 justify-end">
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(c.id)} data-testid={`client-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle><DialogDescription>Gerencie status, contrato e acessos liberados para este cliente.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input data-testid="client-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="client-select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notas</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            {editing && (
              <>
                <div><Label>Link do contrato</Label><Input value={form.contract_url || ""} onChange={(e) => setForm({ ...form, contract_url: e.target.value })} /></div>
                <div>
                  <Label>Status do contrato</Label>
                  <Select value={form.contract_status} onValueChange={(v) => setForm({ ...form, contract_status: v })}>
                    <SelectTrigger data-testid="client-select-contract"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_sent">Não enviado</SelectItem>
                      <SelectItem value="sent">Enviado</SelectItem>
                      <SelectItem value="signed">Assinado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Itens liberados no acesso do cliente</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addPackItem} data-testid="client-add-pack-item">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.pack_items.map((item, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input placeholder="Título" value={item.title} onChange={(e) => updatePackItem(i, "title", e.target.value)} />
                        <Input placeholder="URL" value={item.url} onChange={(e) => updatePackItem(i, "url", e.target.value)} />
                        <Button size="icon" variant="ghost" onClick={() => removePackItem(i)} data-testid={`client-remove-pack-item-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 flex items-center gap-2 text-sm text-black/60">
                  <LinkIcon className="w-4 h-4" /> {portalUrl(form.portal_token)}
                </div>
              </>
            )}
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="client-save-button" className="mt-4">Salvar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
